import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Trophy, Users, Calendar, ArrowRight } from "lucide-react";
import { stateGuides } from "@/data/stateGuides";

interface Props {
  slug?: string;
}

/**
 * Rich SEO content rendered above the topic list on state-level forum pages
 * like /youth-soccer-forum-usa/new-jersey-youth-soccer. Returns null when no
 * guide matches the slug — non-state forum pages are unaffected.
 */
export const StateForumSeoContent: React.FC<Props> = ({ slug }) => {
  if (!slug) return null;
  const guide = stateGuides[slug];
  if (!guide) return null;

  const canonical = `https://minorsoccertalks.com/youth-soccer-forum-usa/${slug}`;
  const title = `Talking Soccer ${guide.abbreviation} — ${guide.state} Youth Soccer Forum & Guide`;
  const description = `${guide.state} youth soccer forum and guide: ECNL, MLS Next, tryouts, top clubs and tournaments. Talking soccer ${guide.state} parents trust.`.slice(
    0,
    160,
  );

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `Talking Soccer ${guide.abbreviation}: ${guide.state} Youth Soccer Guide`,
    about: `${guide.state} youth soccer`,
    inLanguage: "en-US",
    url: canonical,
    publisher: {
      "@type": "Organization",
      name: "Minor Soccer Talks",
      url: "https://minorsoccertalks.com",
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: guide.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <section
        aria-label={`${guide.state} youth soccer guide`}
        className="space-y-8 mb-6"
      >
        <p className="text-base text-muted-foreground leading-relaxed">
          {guide.heroTagline}
        </p>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Top {guide.state} Leagues &amp; Pathways
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {guide.leagues.map((l) => (
              <Card key={l.name} className="p-4">
                <h3 className="font-semibold mb-1 text-sm sm:text-base">
                  {l.name}
                </h3>
                <p className="text-sm text-muted-foreground">{l.description}</p>
              </Card>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            {guide.state} Tryout Schedules
          </h2>
          <Card className="p-4">
            <p className="text-sm sm:text-base text-muted-foreground">
              {guide.tryoutWindows}
            </p>
          </Card>
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Regional Tournaments &amp; Showcases
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {guide.tournaments.map((t) => (
              <Card key={t.name} className="p-4">
                <h3 className="font-semibold mb-1 text-sm sm:text-base">
                  {t.name}
                </h3>
                <p className="text-sm text-muted-foreground">{t.description}</p>
              </Card>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            College Recruiting Pipeline
          </h2>
          <Card className="p-4">
            <p className="text-sm sm:text-base text-muted-foreground">
              {guide.collegePipeline}
            </p>
          </Card>
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4">
            {guide.state} Youth Soccer FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {guide.faqs.map((faq, idx) => (
              <AccordionItem key={faq.question} value={`item-${idx}`}>
                <AccordionTrigger className="text-left text-sm sm:text-base font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Auto-generated forum links — point to real level-3 subcategories
            that exist for every state ({state}-u{age}-{league}). */}
        {(() => {
          const stateSlug = slug.replace(/-youth-soccer$/, "");
          const base = `/youth-soccer-forum-usa/${slug}`;
          const ageGroupLinks = [
            { label: `${guide.abbreviation} U13 ECNL`, to: `${base}/${stateSlug}-u13-ecnl` },
            { label: `${guide.abbreviation} U15 ECNL`, to: `${base}/${stateSlug}-u15-ecnl` },
            { label: `${guide.abbreviation} U17 ECNL`, to: `${base}/${stateSlug}-u17-ecnl` },
            { label: `${guide.abbreviation} U14 MLS Next`, to: `${base}/${stateSlug}-u14-mls-next` },
            { label: `${guide.abbreviation} U16 MLS Next`, to: `${base}/${stateSlug}-u16-mls-next` },
            { label: `${guide.abbreviation} U13 Girls Academy`, to: `${base}/${stateSlug}-u13-girls-academy-ga` },
          ];
          return (
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold mb-3">
                Join the {guide.state} Conversation
              </h2>
              <p className="text-sm text-muted-foreground mb-3">
                Jump into the most-active {guide.state} age-group and league
                forums:
              </p>
              <div className="flex flex-wrap gap-2">
                {ageGroupLinks.map((link) => (
                  <Button
                    key={link.to}
                    asChild
                    variant="outline"
                    size="sm"
                  >
                    <Link to={link.to} aria-label={`Go to ${link.label} forum`}>
                      {link.label}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                ))}
              </div>
            </div>
          );
        })()}
      </section>
    </>
  );
};

export default StateForumSeoContent;
